import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useTwilioVoice } from "@/context/TwilioVoiceContext";
import { Send, MessageSquare, Phone } from "lucide-react";

export default function Chat() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const tv = useTwilioVoice();
  const L = lang === "el";
  const [threads, setThreads] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const isOwner = user?.role === "company_owner";

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data || []);
      if (!selectedId && data?.length) setSelectedId(data[0].peer.id);
    } catch { /* ignore */ }
  };

  const loadMessages = async (peerId) => {
    if (!peerId) { setMessages([]); return; }
    try {
      const { data } = await api.get(`/messages?with_user_id=${peerId}`);
      setMessages(data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadThreads(); }, []);

  useEffect(() => {
    if (!selectedId) return;
    loadMessages(selectedId);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadMessages(selectedId), 5000);
    return () => clearInterval(pollRef.current);
  }, [selectedId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const send = async (e) => {
    e?.preventDefault();
    const body = draft.trim();
    if (!body || !selectedId || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages", { to_user_id: selectedId, body });
      setMessages((m) => [...m, data]);
      setDraft("");
      loadThreads();
    } finally { setSending(false); }
  };

  const selectedPeer = threads.find((t) => t.peer.id === selectedId)?.peer;

  return (
    <div className="space-y-4" data-testid="chat-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{L ? "Επικοινωνία" : "Communication"}</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">
          {isOwner ? (L ? "Επικοινωνία με πωλητή" : "Chat with rep") : (L ? "Επικοινωνία με ιδιοκτήτη" : "Chat with owner")}
        </h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden grid grid-cols-1 md:grid-cols-[280px_1fr] h-[70vh]">
        {/* Thread list */}
        <aside className="border-r border-slate-200 overflow-y-auto bg-slate-50/50">
          {threads.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              <MessageSquare className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              {isOwner
                ? (L ? "Δεν υπάρχουν πωλητές ακόμη" : "No sales reps yet")
                : (L ? "Δεν έχει οριστεί ιδιοκτήτης" : "No owner assigned")}
            </div>
          ) : threads.map((t) => (
            <button
              key={t.peer.id}
              data-testid={`chat-thread-${t.peer.id}`}
              onClick={() => setSelectedId(t.peer.id)}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors ${
                selectedId === t.peer.id ? "bg-white" : "hover:bg-white"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-slate-900 text-sm truncate">{t.peer.name}</div>
                {t.unread > 0 && (
                  <span className="bg-[#0052CC] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {t.unread}
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 truncate mt-0.5">
                {t.last_message ? t.last_message.body : (L ? "Καμία συνομιλία" : "No messages yet")}
              </div>
            </button>
          ))}
        </aside>

        {/* Conversation */}
        <section className="flex flex-col min-h-0">
          {!selectedId ? (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400">
              {L ? "Επιλέξτε συνομιλία" : "Pick a conversation"}
            </div>
          ) : (
            <>
              <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-slate-900">{selectedPeer?.name}</div>
                  <div className="text-xs text-slate-500">{selectedPeer?.email}</div>
                </div>
                {tv.enabled && selectedPeer && (
                  <button
                    data-testid="chat-call-peer"
                    onClick={() => tv.startCall(`user:${selectedPeer.id}`)}
                    disabled={!tv.deviceReady || tv.outOfMinutes || tv.status !== "idle"}
                    title={tv.outOfMinutes ? (L ? "Τέλος λεπτών" : "Out of minutes") : (L ? "Κλήση" : "Call")}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:bg-slate-400"
                  >
                    <Phone className="w-4 h-4" /> {L ? "Κλήση" : "Call"}
                  </button>
                )}
              </header>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="chat-messages">
                {messages.length === 0 ? (
                  <div className="text-center text-sm text-slate-400 py-8">
                    {L ? "Στείλτε το πρώτο μήνυμα" : "Send the first message"}
                  </div>
                ) : messages.map((m) => {
                  const mine = m.from_user_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${
                        mine ? "bg-[#0052CC] text-white" : "bg-slate-100 text-slate-900"
                      }`}>
                        <div className="whitespace-pre-wrap break-words">{m.body}</div>
                        <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-slate-500"}`}>
                          {new Date(m.created_at).toLocaleString(L ? "el-GR" : "en-GB", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={send} className="border-t border-slate-200 px-4 py-3 flex items-center gap-2">
                <input
                  data-testid="chat-input"
                  type="text"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={L ? "Γράψτε μήνυμα..." : "Type a message..."}
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/40 focus:border-[#0052CC]"
                />
                <button
                  type="submit"
                  data-testid="chat-send"
                  disabled={sending || !draft.trim()}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold hover:bg-[#0043A8] disabled:opacity-40"
                >
                  <Send className="w-4 h-4" /> {L ? "Αποστολή" : "Send"}
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
